import { call, put, select, takeEvery } from 'redux-saga/effects'

import * as Storage from '@app/lib/storage'
import type { AppAction, AppState } from '@app/state'
import * as Auth from '@app/state/authenticate'

import * as Init from '../init'

import * as Requests from './rest'
import * as Config from './state'

import type { ClientConfig, IApiConfig, IClinicConfig } from './interfaces'
import type { AxiosResponse } from 'axios'

// API Location Setting
//
export function* handleConfigSetLocation() {
  yield takeEvery(Config.ActionType.SetApiLocation, setLocation)
}

export function* setLocation(action: AppAction) {
  if (action.type === Config.ActionType.SetApiLocation) {
    if (action.location !== null) {
      yield call(Storage.setItem, Storage.API_LOCATION, action.location)
    } else {
      yield call(Storage.clearItem, Storage.API_LOCATION)
    }
  }
}

// Client Config Fetching
//
export function* handleConfigClientFetch() {
  yield takeEvery(Config.ActionType.FetchClientConfig, fetchClientConfig)
}

export function* fetchClientConfig() {
  yield put(Config.actions.fetchClientConfigStarted())
  try {
    const request = Requests.fetchClientConfig()
    const response: AxiosResponse<ClientConfig> = yield call(request, '')
    yield put(Config.actions.fetchClientConfigSuccess(response.data))
    yield put(Init.actions.configureComplete())
  } catch (e) {
    yield put(Config.actions.fetchClientConfigFailure())
  }
}

// API Config Fetching
//
export function* handleConfigApiFetch() {
  yield takeEvery(Config.ActionType.FetchApiConfig, fetchApiConfig)
}

export function* selectCanFetchAndLocationState() {
  const appState: AppState = yield select((a) => a)
  const maybeApiLocation = Config.queries.getApiLocation(appState.config)

  if (maybeApiLocation !== null) {
    return {
      canFetch: true,
      apiLocation: maybeApiLocation,
    }
  } else {
    return {
      canFetch: false,
      apiLocation: null,
    }
  }
}

export function* selectIsAuthenticated() {
  const appState: AppState = yield select((a) => a)
  const isAuthenticated = Auth.queries.isAuthenticated(appState.auth)

  if (isAuthenticated) {
    return {
      isAuthenticated: true,
      authToken: appState.auth.token,
    }
  } else {
    return {
      isAuthenticated: false,
      authToken: null,
    }
  }
}

export function* fetchApiConfig() {
  const { canFetch, apiLocation } = yield call(selectCanFetchAndLocationState)
  if (canFetch) {
    yield put(Config.actions.fetchApiConfigStarted())
    try {
      const request = Requests.fetchApiConfig()
      const response: AxiosResponse<IApiConfig> = yield call(
        request,
        apiLocation,
      )
      yield put(Config.actions.fetchApiConfigSuccess(response.data))
    } catch {
      yield put(Config.actions.fetchApiConfigFailure())
    }
  } else {
    yield put(Config.actions.fetchApiConfigStarted())
    yield put(Config.actions.fetchApiConfigFailure())
  }
}

export function* handleConfigClinicFetch() {
  yield takeEvery(Config.ActionType.FetchClinicConfig, fetchClinicConfig)
}

export function* fetchClinicConfig() {
  const { canFetch, apiLocation } = yield call(selectCanFetchAndLocationState)
  const { isAuthenticated, authToken } = yield call(selectIsAuthenticated)
  if (canFetch && isAuthenticated) {
    yield put(Config.actions.fetchClinicConfigStarted())
    try {
      const request = Requests.fetchClinicConfig()
      const response: AxiosResponse<IClinicConfig> = yield call(
        request,
        apiLocation,
        authToken,
      )
      yield put(Config.actions.fetchClinicConfigSuccess(response.data))
    } catch {
      yield put(Config.actions.fetchClinicConfigFailure())
    }
  } else {
    yield put(Config.actions.fetchClinicConfigFailure())
  }
}

// Fetch Success
//
export function* selectConfig() {
  const appState: AppState = yield select((a) => a)
  return appState.config.api
}

export const sagas: { [key: string]: () => IterableIterator<unknown> } = {
  handleConfigSetLocation,
  handleConfigClientFetch,
  handleConfigApiFetch,
  handleConfigClinicFetch,
}
